import { DateTime } from "luxon";
import { map, groupBy, forOwn, pickBy } from "../../utils/lodash";
import { Log } from "../../utils/logging";
import { isNullOrEmpty } from "../../utils";
import { InterviewState } from "../../models/states/InterviewState";
import { DispositionCode } from "../../models/enums/DispositionCode";
import { LookupTableParams } from "../../models/LookupTableParams";
import { createRespondent } from "../../models/factories/respondentFactory";
import {
    getSettingString,
    getSettingBoolean,
    getSettingEnum,
} from "../../models/utilities/settingUtilities";
import {
    __isBlockVisible__,
    __isQuestionVisible__,
    __evaluateSafe__,
} from "../../expressions/parser";
import { QuotaModel } from "../../models/QuotaModel";
import { QuotaStatus } from "../../models/enums/QuotaStatus";
import { createQuota } from "../../models/factories/quotaFactory";
import { quotasReducer } from "../reducers/interview/quotasReducer";
import { QuotaMetOrCloseActions } from "../../models/enums/QuotaMetOrCloseAction";
import {
    isExpired,
    getCompletionQuestion,
    getQuestionWithVariable,
    getTerminationOptions,
    getQuestionWithId,
} from "../../models/utilities/questionnaireUtilities";
import { createQuestionnaire } from "../../models/factories/questionnaireFactory";
import { translate } from "../../models/utilities/translationUtilities";
import { NavigationResultModel } from "../../models/NavigationResultModel";
import {
    MiddlewareType,
    DispatchType,
    StoreType,
    NavigationShortcutType,
    SetVariableMentionsType,
} from "../actions/types";
import {
    CONNECTION_START,
    AUDIO_RECORDING_START,
    AUDIO_RECORDING_STOP,
    LOOKUP_TABLE_FETCH,
    NAVIGATION_SHORTCUT,
    NAVIGATION_PREVIOUS_PAGE,
    NAVIGATION_NEXT_PAGE,
    NAVIGATION_QUIT,
    setNavigationEnabled,
    lookupTableReceive,
    deviceChanged,
    respondentChanged,
    quotasChanged,
    questionnaireChanged,
    setLoopRow,
    navigationQuit,
    changeLanguage,
    connectionConnected,
    navigationNextPage,
    quotasCloseStratum,
    quotasCloseUserStratum,
    navigationDisplayMessage,
    validationSetResult,
    serverPageChanged,
    setServerTrackedAnswers,
    NAVIGATION_PREVIEW_QUESTION,
    NAVIGATION_BRANCH_TO,
    SET_VARIABLE_MENTIONS,
    loadAnswersDto,
    VALIDATE_PAGE_ON_SERVER,
    refreshCurrentPageQuestions,
    validationClear,
    setCoordinates,
    respondentSetDispositionCode,
    LOOKUP_TABLE_ADD_CHOICE_ITEM,
} from "../actions";
import { BranchToOptions } from "../../models/BranchToOptions";
import { setVariableMention } from "../reducers/interview/variablesUtilities";
import { VariablesState } from "../../models/states/VariablesState";
import { NavigationState } from "../../models/states/NavigationState";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { StoreState } from "../../models/states/StoreState";
import {
    isVariableValidatedServerSide,
    validationErrorConverter,
} from "../../models/utilities/validationUtilities";
import { trackEvent } from "../../utils/Raygun";
import { currentPageVariableDependencies } from "../reducers/interview/navigationUtilities";
import { TerminationAction } from "../../models/enums/TerminationAction";
import { pageReady } from "./flowController";
import { QueryParameters } from "../../consts";
import { applyCSS, generateCSS } from "../../utils/dynamic-css/coreDynamicCss";
import { selectNavigationDisabled } from "../selectors/interview/navigation";
import { createDispositionCode } from "../../models/factories/dispositionCodeFactory";

/**
 * DEPRECATED: Please refer to thunks.ts as middlewares are not a good practice for connecting to an api.
 */

const TAG = "apiConnector";
const RECONNECTION_TIMEOUT = 600;

let apiUrl: string = null;
let psid: string = null;
let skey: string = null;
let lastScriptNodeIndex: number = -1;

export type DoneCallback = (result: any, code: number, time: number) => void;

interface SendOptions {
    data?: any;
    onDone?: DoneCallback;
    retry?: boolean;
    disableNavigation?: boolean;
}

function makeApiInterface(store: StoreType) {
    const send = (method: string, path: string, options: SendOptions = {}) => {
        const { data, onDone, retry, disableNavigation = true } = options;
        if (!path.startsWith("/")) path = `/${path}`;
        const startTime = new Date().getTime();
        if (!apiUrl) return;
        const url = apiUrl + path + (psid ? `&${QueryParameters.PAGE_SESSION_ID}=${psid}` : "");
        Log.d(TAG, `${method} ${url}`, "...");
        if (disableNavigation) {
            store.dispatch(setNavigationEnabled(false));
        }
        (function performRequest() {
            const xhr = new XMLHttpRequest();
            xhr.open(method, url, true);
            xhr.addEventListener("load", () => {
                const time = new Date().getTime() - startTime;
                trackEvent(url, time);
                if (xhr.readyState === 4) {
                    Log.d(TAG, `${method} ${url}`, `${xhr.status} (${time}ms)`);
                    let result: string | any = xhr.responseText;
                    if (!isNullOrEmpty(xhr.responseText)) {
                        try {
                            result = JSON.parse(result);
                        } catch (e) {
                            Log.e(TAG, "XHR_ERROR", undefined, e);
                        }
                    }
                    if (result && result.isLoading) {
                        setTimeout(performRequest, RECONNECTION_TIMEOUT);
                        return;
                    }
                    if (onDone) {
                        onDone(result, xhr.status, time);
                    }
                    if (disableNavigation) {
                        store.dispatch(setNavigationEnabled(true));
                    }
                }
            });
            xhr.addEventListener("error", (e) => {
                const time = new Date().getTime() - startTime;
                trackEvent(url, time);
                if (retry) {
                    setTimeout(performRequest, RECONNECTION_TIMEOUT);
                    return;
                }
                Log.e(TAG, `${method} ${url}`, `(${time}ms)`, e);
                if (onDone) onDone(xhr.statusText, 0, time);
                store.dispatch(setNavigationEnabled(true));
            });
            const tokenElement = document.getElementsByName(
                "__RequestVerificationToken",
            )[0] as HTMLInputElement;
            const token = tokenElement?.value || "";
            xhr.setRequestHeader("X-CSRF-TOKEN", token);
            if (data != null) {
                const json = JSON.stringify(data);
                xhr.setRequestHeader("Content-Type", "application/json");
                xhr.send(json);
            } else {
                xhr.send();
            }
        })();
    };

    const get = (path: string, options?: SendOptions) => send("GET", path, options);
    const post = (path: string, options?: SendOptions) => send("POST", path, options);
    const del = (path: string, options?: SendOptions) => send("DELETE", path, options);
    const put = (path: string, options?: SendOptions) => send("PUT", path, options);

    return { get, post, put, del };
}

// Converts quotas state to quotas DTO.
const quotasDto = (quotas: QuotaModel[]): any[] =>
    map(quotas, (quota) => ({
        ...quota,
        status: QuotaStatus[quota.status],
        userStatus: QuotaStatus[quota.userStatus],
    }));

// Converts answer state to answers DTO for sending to A4S.
const answersDto = (
    state: StoreState,
    interviewVariables: VariablesState = state.interview.variables,
) => {
    const { navigation, questionnaire } = state.interview;
    let { mentionsByVariableName } = interviewVariables;

    if (state.application.mode === SurveyMode.Normal) {
        mentionsByVariableName = getCurrentPageMentions(navigation, interviewVariables);
    }

    return map(mentionsByVariableName, (mentions, variableName) => ({
        variableName,
        questionType: getQuestionWithVariable(questionnaire, variableName)?.type,
        matrices: map(groupBy(mentions, "matrix"), (m, matrixIndex) => ({
            index: matrixIndex,
            mentions: m.map((mention) => ({
                index: mention.index,
                value: mention.value,
                openEnd: mention.openEnd,
            })),
        })),
    }));
};

const getCurrentPageMentions = (
    navigation: NavigationState,
    interviewVariables: VariablesState,
) => {
    const currentPageVariables = getPageVariables(navigation);
    return pickBy(interviewVariables.mentionsByVariableName, (_m, variableName) =>
        currentPageVariables?.some(
            (pageVariable) => pageVariable.toLocaleLowerCase() === variableName.toLocaleLowerCase(),
        ),
    );
};

export const ApiBroadcaster = (store: StoreType) => {
    const { get, post, del } = makeApiInterface(store);

    return {
        audioRecordingStart(questionName: string, matrix: number) {
            get(`r/audiorecording/start?questionName=${questionName}&matrix=${matrix}`);
        },
        audioRecordingStop() {
            get("r/audiorecording/stop?");
        },
        clearFile(variableName: string, matrix: number) {
            del(`r/clear?variableName=${variableName}&matrix=${matrix}`);
        },
        uploadFile(variableName: string, file: File) {
            if (file) {
                const xhr = new XMLHttpRequest();
                xhr.open("POST", `/Upload?${QueryParameters.PAGE_SESSION_ID}=${psid}`, true);
                // xhr.setRequestHeader("Content-type", "file");
                const data = new FormData();
                data.append("file", file);
                xhr.send(data);
            }
        },
        fileUploadOpen(variableName: string, mimeType: string, matrix: number) {
            get(
                `r/filedialog/open?variableName=${variableName}&mimeType=${mimeType}&matrix=${matrix}`,
            );
        },
        openUploadedFile(variableName: string, matrix: number) {
            get(`r/openUploadedFile?variableName=${variableName}&matrix=${matrix}`);
        },
        lookupTableFetch(
            tableName: string,
            requestId: string,
            params: LookupTableParams,
            questionName: string,
        ) {
            const { interview } = store.getState();
            const language = interview.respondent.language || interview.language;
            get(
                encodeURI(
                    `s/lookuptable?pageStart=${params.pageStart}&${QueryParameters.LANGUAGE}=${language}&pageLength=${params.pageLength}` +
                        `&search=${params.search}&sortColumn=${params.sortColumn}&sqlFilter=${params.sqlFilter}&sortAsc=${params.sortAsc}` +
                        `&selected=${params.selected.join(
                            ",",
                        )}&questionName=${questionName}&tableName=${tableName}&requestId=${requestId}&columnFilters=${JSON.stringify(
                            params.columnFilters,
                        )}&includeSelected=${params.includeSelected}&excludeSelected=${
                            params.excludeSelected
                        }`,
                ),
                {
                    onDone: (resume, code) => {
                        if (code !== 200) {
                            Log.e(
                                TAG,
                                "LOOKUP_TABLE_ERROR",
                                "An error occurred fetching the table.",
                            );
                            return;
                        }
                        const page = resume && resume.hasOwnProperty("page") ? resume.page : null;
                        if (page)
                            store.dispatch(
                                lookupTableReceive(tableName, requestId, page, questionName),
                            );
                    },
                },
            );
        },
        addChoiceItem(
            questionName: string,
            tableName: string,
            formData: Record<string, string>,
            onDone?: DoneCallback,
        ) {
            Log.d(TAG, "Creating a new choic item");
            post("s/addLookupTableName?", {
                data: {
                    formdata: JSON.stringify(formData),
                    questionName,
                    tableName,
                },
                onDone,
            });
        },
        quit(
            interview: InterviewState,
            disposition?: DispositionCode,
            options?: {
                message?: string;
                closedStrata?: boolean;
                redirectUrl?: string;
                forcedQuit?: boolean;
                discard?: boolean;
                questionId?: number;
            },
        ) {
            const params: { [key: string]: string | boolean | number } = {};
            if (options && options.closedStrata) params.closedStrata = true;
            if (options && options.redirectUrl) params.redirectUrl = options.redirectUrl;
            if (options && options.message) params.message = options.message;
            if (options && options.forcedQuit) params.forcedQuit = true;
            if (options && options.discard) params.discard = true;
            if (options && options.questionId) params.questionId = options.questionId;
            if (disposition) params.disposition = disposition;
            params.language = interview.language;
            if (params.forcedQuit)
                get(
                    `r/quit?${map(
                        params,
                        (value, key) => `${key}=${encodeURIComponent(value.toString())}`,
                    ).join("&")}`,
                );
        },
        back(onDone?: DoneCallback) {
            const state = store.getState();
            const { mode } = state.application;
            const answers =
                mode === SurveyMode.Preview || mode === SurveyMode.Test ? answersDto(state) : null;
            post("r/back?", {
                onDone,
                data: {
                    answers,
                },
            });
        },
        shortcut(shortcut: NavigationShortcutType, onDone?: DoneCallback) {
            post("r/shortcut?", { data: shortcut, onDone });
        },
        navigateTo(branchOptions: BranchToOptions, onDone?: DoneCallback) {
            const state = store.getState();
            const { mode } = state.application;
            const answers =
                mode === SurveyMode.Preview || mode === SurveyMode.Test ? answersDto(state) : null;

            post("r/navigateTo?", {
                data: {
                    destination: branchOptions.destination,
                    isQuitButton: branchOptions.isQuitButton,
                    isPreviewNavigation: branchOptions.isPreviewNavigation,
                    answers,
                },
                onDone,
            });
        },
        next(onDone?: DoneCallback) {
            const { interview } = store.getState();
            Log.d(TAG, "Saving interview state...");
            const completionQuestion = getCompletionQuestion(interview.questionnaire);
            const hasCompletionQuestion =
                completionQuestion &&
                __isBlockVisible__(
                    interview.questionnaire.blocks[completionQuestion.blockIndex],
                    interview,
                ) &&
                __isQuestionVisible__(completionQuestion, interview);
            post("r/next?", {
                data: {
                    quotas: quotasDto(interview.quotas.output),
                    answers: answersDto(store.getState()),
                    hasCompletionQuestion,
                    completionQuestionReached: interview.navigation.completionQuestionReached,
                    respondent: interview.respondent,
                    matrixByBlockName: interview.navigation.loopByBlockName,
                },
                onDone,
            });
        },
        setMentions(action: SetVariableMentionsType, onDone?: DoneCallback) {
            const { interview } = store.getState();
            Log.d(TAG, "Saving variable mentions...");
            const newVariableState = setVariableMention(
                interview.variables,
                interview.navigation,
                interview.questionnaire,
                action.variableName,
                action.matrixIndex,
                action.builder,
            );
            const variableDependencies = currentPageVariableDependencies(interview.navigation);
            if (variableDependencies.includes(action.variableName)) {
                post("r/setMentions?", {
                    disableNavigation: false,
                    data: answersDto(store.getState(), newVariableState),
                    onDone,
                });
            }
        },
        validatePage(onDone?: DoneCallback) {
            const { interview } = store.getState();
            Log.d(TAG, "Validating page on server");
            post("r/validatePage?", {
                disableNavigation: false,
                data: {
                    answers: answersDto(store.getState()),
                    respondent: interview.respondent,
                    matrixByBlockName: interview.navigation.loopByBlockName,
                },
                onDone,
            });
        },
    };
};

export const getPageVariables = (navigation: NavigationState): string[] =>
    navigation?.currentPageOnServer?.questions?.flatMap((q) => q?.variables.flatMap((v) => v.name));

const executeActionForResult = (
    next: DispatchType,
    result: NavigationResultModel,
    action?: any,
) => {
    if (result && result.answers) {
        next(loadAnswersDto(result.answers));
    }
    if (result.currentPage) {
        next(serverPageChanged(result.currentPage));
    }
    if (result.respondent) {
        next(
            respondentSetDispositionCode(createDispositionCode(result.respondent.dispositionCode)),
        );
        if (result.respondent.language) {
            next(changeLanguage(result.respondent.language));
        }
    }
    if (result.termination) {
        const options = {
            terminationSurveyAction: result.termination.action,
            redirectUrl: result.termination.value,
            message: result.termination.value,
        };
        next(navigationQuit(result.termination.dispositionCode, options));
    }
    if (action) next(action);
};

// Connects to an api.
const start = (url: string, next: DispatchType, store: StoreType) => {
    const { get } = makeApiInterface(store);
    apiUrl = url;
    if (apiUrl.endsWith("/")) apiUrl = apiUrl.slice(0, -1);
    psid = document.querySelector<HTMLInputElement>("#S2_SessionId")?.value;
    skey = document.querySelector<HTMLInputElement>("#S2_SessionId_Skey")?.value;
    url = getReloadSurveyUrl();
    history.replaceState({}, "", url);
    const date = DateTime.now();
    get(`preflight?=${new Date().getTime()}&dtoffset=${date.offset}`, {
        onDone: async (ready, code) => {
            if (code !== 200) {
                Log.e(TAG, "PREFLIGHT_ERROR", "An error occurred executing preflight.");
                return;
            }

            if (ready && ready.termination) {
                const { action: terminationAction, value } = ready.termination;
                switch (terminationAction) {
                    case TerminationAction.DisplayMessage:
                        store.dispatch(navigationDisplayMessage(value));
                        break;
                    case TerminationAction.RedirectToUrl: {
                        const redirectUrl = value.indexOf("://") === -1 ? `http://${value}` : value;
                        window.location.href = redirectUrl;
                        break;
                    }
                    default:
                        store.dispatch(navigationDisplayMessage(value));
                        break;
                }
                return;
            }

            store.dispatch(deviceChanged(ready.device));
            store.dispatch(respondentChanged(createRespondent(ready.respondent)));
            store.dispatch(quotasChanged(ready.strata));
            const isSimulated = ready.respondent.simulated;
            let invalidateCacheArg = ""; // invalidate caching of survey for preview
            if (ready.respondent.surveyVersion === 0) {
                invalidateCacheArg = `=${new Date().getTime()}`;
            }
            if (ready.surveyTitle) {
                document.title = ready.surveyTitle;
            }
            if (ready.preventIndexing) {
                const noindex = document.createElement("meta");
                noindex.name = "robots";
                noindex.content = "noindex";
                document.head.appendChild(noindex);

                const nofollow = document.createElement("meta");
                nofollow.name = "robots";
                nofollow.content = "nofollow";
                document.head.appendChild(nofollow);
            }

            if (!isNullOrEmpty(ready.customHead)) {
                await new Promise<void>((resolve) => {
                    document.head.innerHTML += ready.customHead;
                    nodeScriptReplace(document.head);
                    if (lastScriptNodeIndex > -1) {
                        (document.head.childNodes[
                            lastScriptNodeIndex
                        ] as HTMLScriptElement).onload = () => resolve();
                    } else {
                        resolve();
                    }
                });
            }
            getNewSurveyFromApi(get, ready, invalidateCacheArg, store, isSimulated, next, date);
        },
        retry: true,
    });
};

/**
 * Closes half-open or open strata which have passed their objective.
 * Returns true if a stratum has been closed.
 */
const closeStrata = (store: StoreType) => {
    const { interview } = store.getState();
    // Simulate quotas without going through all reducers.
    const state = quotasReducer(interview.quotas, interview, navigationNextPage());
    // Close all stratas before returning.
    const closedStrata = [];
    for (const quota of state.output) {
        // Never consider stratum 0 as it is managed by container.
        if (quota.stratumId === 0) continue;
        // Evaluate condition lazily in case we have to evaluate twice.
        let evaluated: boolean;
        const evaluateLazy = () => {
            if (!evaluated) evaluated = __evaluateSafe__(interview, quota.condition).asBoolean();
            return evaluated;
        };
        // If the stratum has to be closed
        const objectiveSurpassed =
            quota.status === QuotaStatus.Closed || quota.frequency > quota.objective;
        const halfOpen = quota.status === QuotaStatus.HalfOpen;
        const userObjectiveSurpassed =
            quota.userStatus === QuotaStatus.Closed ||
            (quota.userObjective > 0 && quota.userFrequency > quota.userObjective);
        const userHalfOpen = quota.userStatus === QuotaStatus.HalfOpen;
        if ((objectiveSurpassed || halfOpen) && evaluateLazy()) {
            store.dispatch(quotasCloseStratum(quota.stratumId));
            closedStrata.push(quota);
        } else if ((userObjectiveSurpassed || userHalfOpen) && evaluateLazy()) {
            store.dispatch(quotasCloseUserStratum(quota.stratumId));
            closedStrata.push(quota);
        }
    }
    // Return true if any stratum is closed.
    return closedStrata.length > 0;
};

/**
 * Connects to a websocket server.
 */
export const apiConnector: MiddlewareType = (store) => (next) => (action) => {
    switch (action.type) {
        case CONNECTION_START:
            // Start the connection.
            start(action.url, next, store);
            break;
        case AUDIO_RECORDING_START:
            ApiBroadcaster(store).audioRecordingStart(action.questionName, action.matrix);
            break;
        case AUDIO_RECORDING_STOP:
            ApiBroadcaster(store).audioRecordingStop();
            break;
        case LOOKUP_TABLE_FETCH: {
            const result = next(action);
            const requestId = store.getState().lookupTable.requestId[action.questionName];
            ApiBroadcaster(store).lookupTableFetch(
                action.tableId,
                requestId,
                action.params,
                action.questionName,
            );
            return result;
        }
        case LOOKUP_TABLE_ADD_CHOICE_ITEM: {
            const result = next(action);
            ApiBroadcaster(store).addChoiceItem(
                action.questionName,
                action.tableName,
                action.params,
            );
            return result;
        }
        case NAVIGATION_SHORTCUT: {
            ApiBroadcaster(store).shortcut(action, (result: NavigationResultModel) =>
                executeActionForResult(next, result, action),
            );
            return null;
        }
        case NAVIGATION_PREVIOUS_PAGE: {
            ApiBroadcaster(store).back((result: NavigationResultModel) => {
                if (result && result.strata) {
                    store.dispatch(quotasChanged(result.strata));
                }
                executeActionForResult(next, result, action);
            });
            return null;
        }
        case NAVIGATION_NEXT_PAGE: {
            const { interview } = store.getState();
            // Iterate over quotas to exit out of quota surveys.
            if (
                closeStrata(store) &&
                getSettingBoolean(
                    interview.questionnaire,
                    "CLOSED_STRATA_RULE",
                    "BlockAllowInterviews",
                )
            ) {
                // Exit with closedStrata: true so a warning can be displayed depending on survey rules.
                const closeAction = getSettingEnum(
                    interview.questionnaire,
                    QuotaMetOrCloseActions,
                    "QUOTA_ACTION",
                );
                switch (closeAction) {
                    case QuotaMetOrCloseActions.DisplayMessageAndExit: {
                        const message = translate(
                            interview.questionnaire,
                            "QuotaMetClosedMessage",
                            interview.respondent.language,
                        );
                        store.dispatch(navigationDisplayMessage(message));
                        break;
                    }
                    case QuotaMetOrCloseActions.RedirectToUrl: {
                        const redirectUrl = getSettingString(
                            interview.questionnaire,
                            "QUOTA_ACTION_REDIRECT_URL",
                        );
                        window.location.href = redirectUrl;
                        break;
                    }
                    default:
                        store.dispatch(navigationQuit(DispositionCode.OutOfQuota));
                }
                // Prevent further navigation.
                return null;
            }

            ApiBroadcaster(store).next((result: NavigationResultModel) => {
                if (result && result.strata) {
                    store.dispatch(quotasChanged(result.strata));
                }
                if (result.errors && result.errors.length) {
                    const validationResult = validationErrorConverter(result.errors);
                    store.dispatch(validationClear(true, true));
                    store.dispatch(validationSetResult(validationResult));
                    executeActionForResult(next, result);
                    return;
                }
                if (result && result.termination) {
                    const { action: terminationAction, value } = result.termination;
                    switch (terminationAction) {
                        case TerminationAction.DisplayMessage:
                            store.dispatch(
                                navigationQuit(
                                    result.termination.dispositionCode || DispositionCode.Completed,
                                    { message: value, terminationSurveyAction: terminationAction },
                                ),
                            );
                            break;
                        case TerminationAction.RedirectToUrl: {
                            const url = value.indexOf("://") === -1 ? `http://${value}` : value;
                            window.location.href = url;
                            break;
                        }
                        default:
                            executeActionForResult(next, result, action);
                    }
                } else {
                    executeActionForResult(next, result, action);
                    pageReady(store);
                }
            });

            return null;
        }
        case NAVIGATION_QUIT: {
            const { dispositionCode } = action;
            const state = store.getState();
            const options = {
                ...getTerminationOptions(
                    state.interview.questionnaire,
                    state.interview.language,
                    dispositionCode,
                ),
                ...action.options,
            };
            ApiBroadcaster(store).quit(store.getState().interview, dispositionCode, options);
            return next(action);
        }
        case NAVIGATION_BRANCH_TO: {
            ApiBroadcaster(store).navigateTo(action.options, (result: NavigationResultModel) =>
                executeActionForResult(next, result, action),
            );
            break;
        }
        case NAVIGATION_PREVIEW_QUESTION: {
            const questionName = getQuestionWithId(
                store.getState().interview.questionnaire,
                action.questionId,
            )?.name;
            if (questionName) {
                ApiBroadcaster(store).navigateTo(
                    {
                        destination: questionName,
                        isPreviewNavigation: true,
                    },
                    (result: NavigationResultModel) => executeActionForResult(next, result, action),
                );
            }

            break;
        }
        case SET_VARIABLE_MENTIONS: {
            ApiBroadcaster(store).setMentions(action, (result: NavigationResultModel) => {
                if (!selectNavigationDisabled(store.getState())) {
                    next(refreshCurrentPageQuestions(result.currentPage));
                    if (result && result.answers) {
                        next(loadAnswersDto(result.answers));
                    }
                }
            });
            break;
        }
        case VALIDATE_PAGE_ON_SERVER: {
            ApiBroadcaster(store).validatePage((result: NavigationResultModel) => {
                const { navigation, useClientValidation } = store.getState().interview;
                const errors = result.errors?.filter((error) =>
                    isVariableValidatedServerSide(
                        error.variableName.split("$")[0],
                        navigation,
                        useClientValidation,
                    ),
                );
                if (errors && errors.length) {
                    const validationResult = validationErrorConverter(result.errors);
                    store.dispatch(validationSetResult(validationResult));
                }
            });
            break;
        }
        default:
            break;
    }

    return next(action);
};

const nodeScriptReplace = (node) => {
    const scriptTagName = "SCRIPT";
    if (node.tagName === scriptTagName) {
        node.parentNode.replaceChild(nodeScriptClone(node), node);
    } else {
        let i = -1;
        const children = node.childNodes;
        while (++i < children.length) {
            const childNode = children[i];
            nodeScriptReplace(childNode);
            if (
                childNode.tagName === scriptTagName &&
                childNode.attributes.length > 0 &&
                childNode.attributes.src
            ) {
                lastScriptNodeIndex = i;
            }
        }
    }

    return node;
};

const nodeScriptClone = (node) => {
    const script = document.createElement("script");
    script.text = node.innerHTML;

    let i = -1;
    const { attributes } = node;
    let attribute;
    while (++i < attributes.length) {
        script.setAttribute((attribute = attributes[i]).name, attribute.value);
    }
    script.async = false;

    return script;
};
const getNewSurveyFromApi = (
    get: (path: string, options?: SendOptions) => void,
    { respondent, answers, quotas, currentPage }: any,
    invalidateCacheArg: string,
    store: StoreType,
    isSimulated: any,
    next: DispatchType,
    date: DateTime,
) => {
    get(
        `s/${respondent.surveyId}/v/${respondent.surveyVersion}/survey.json?${invalidateCacheArg}`,
        {
            onDone: (data, result) => {
                if (result !== 200) {
                    Log.e(TAG, "SURVEY_ERROR", "An error occurred downloading the survey.");
                    return;
                }

                if (data.theme?.settings?.DynamicCss) {
                    try {
                        const css = generateCSS(
                            data.theme.settings,
                            data.theme.settings.DynamicCss,
                        );

                        applyCSS(css);
                    } catch (e) {
                        Log.e(TAG, "APPLY_CSS_ERROR", "An error occurred applying the css.", e);
                    }
                }

                const questionnaire = createQuestionnaire(data);
                // Check if we should collect the respondent's location.
                const location = getSettingBoolean(
                    questionnaire,
                    "COLLECT_RESPONDENT_GEO_LOCATION",
                );
                // Change the survey.
                store.dispatch(questionnaireChanged(questionnaire));
                // Reset all block loop rows.
                forOwn(respondent.matrixByBlockName, (v, k) => {
                    store.dispatch(setLoopRow(k, v));
                });
                // Check if the survey is expired.
                if (!isSimulated && isExpired(questionnaire)) {
                    // Discard the respondent and close the survey.
                    const message = translate(
                        questionnaire,
                        "InactiveSurveyMessage",
                        store.getState().interview.language,
                    );
                    store.dispatch(
                        navigationQuit(DispositionCode.Dropped, {
                            message,
                            discard: true,
                        }),
                    );
                    return;
                }
                // If we have answers, load them.
                if (answers) {
                    next(loadAnswersDto(answers));
                    store.dispatch(setServerTrackedAnswers(answers));
                }
                // Build the current page on server
                store.dispatch(serverPageChanged(currentPage));
                // If we have quotas, load them.
                if (quotas) {
                    store.dispatch(quotasChanged(quotas.map(createQuota)));
                }
                if (respondent.language) {
                    store.dispatch(changeLanguage(respondent.language));
                }
                // Notify the connection has been established.
                store.dispatch(connectionConnected(apiUrl));

                if (location && navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            const { latitude, longitude } = position.coords;
                            store.dispatch(setCoordinates(latitude, longitude));
                            get(`/r/ready?tzid=${date.zoneName}&lat=${latitude}&lon=${longitude}`);
                        },
                        (error) => {
                            get(
                                `/r/ready?tzid=${date.zoneName}&errormessage=${encodeURIComponent(
                                    error.message,
                                )}`,
                            );
                        },
                    );
                } else {
                    get(`/r/ready?tzid=${date.zoneName}`);
                }
            },
        },
    );
};

const getReloadSurveyUrl = () => {
    const path = "/rs";
    if (!navigator.cookieEnabled)
        return apiUrl + path + (psid ? `?${QueryParameters.PAGE_SESSION_ID}=${psid}` : "");
    return apiUrl + path + (skey ? `?${QueryParameters.SESSION_KEY}=${skey}` : "");
};
